import { useTranslation } from "react-i18next";
import { Icon } from "../icons";

export const DESKTOP_CARD: React.CSSProperties = {
  width: "100%",
  maxWidth: 460,
  margin: "60px auto 0",
  padding: "40px 40px 36px",
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: 18,
  display: "flex",
  flexDirection: "column",
};

export const mobileWrap = (padding: string): React.CSSProperties => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding,
});

export function Caption({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: "var(--muted)",
        letterSpacing: ".08em",
        textTransform: "uppercase",
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

export function Title({ children }: { children: React.ReactNode }) {
  return (
    <h1
      style={{
        fontSize: 30,
        fontWeight: 600,
        margin: 0,
        letterSpacing: "-.02em",
        lineHeight: 1.15,
      }}
    >
      {children}
    </h1>
  );
}

export function Subtitle({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        color: "var(--ink-2)",
        fontSize: 15,
        lineHeight: 1.5,
        margin: "10px 0 28px",
      }}
    >
      {children}
    </p>
  );
}

export function BackButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        color: "var(--ink-2)",
        fontSize: 14,
        fontWeight: 500,
        padding: "4px 0",
        marginBottom: 8,
        alignSelf: "flex-start",
      }}
    >
      {Icon.back()} <span>{t("auth.back")}</span>
    </button>
  );
}

interface FieldProps {
  label: string;
  type?: "text" | "email" | "password" | "number";
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  error?: string | null;
  hint?: string;
  inputMode?: "decimal" | "email" | "text";
  prefix?: React.ReactNode;
  onSubmit?: () => void;
}

export function Field({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoFocus,
  error,
  hint,
  inputMode,
  prefix,
  onSubmit,
}: FieldProps) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          background: "var(--surface)",
          borderRadius: 14,
          border: "1px solid " + (error ? "var(--danger)" : "var(--line)"),
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {prefix}
        <input
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onSubmit) onSubmit();
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          aria-label={label}
          autoComplete={autoCompleteFor(label, type)}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 16,
            fontWeight: 500,
            color: "var(--ink)",
            letterSpacing: "-.01em",
            minWidth: 0,
          }}
        />
      </div>
      <div style={{ minHeight: 18, padding: "6px 4px 0", fontSize: 12 }}>
        {error ? (
          <span style={{ color: "var(--danger)" }}>{error}</span>
        ) : hint ? (
          <span style={{ color: "var(--muted)" }}>{hint}</span>
        ) : null}
      </div>
    </div>
  );
}

export function AuthLegalFooter({
  onOpenPrivacy,
  onOpenTerms,
}: {
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        marginTop: 20,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 6,
        fontSize: 12,
        color: "var(--muted)",
      }}
    >
      <button onClick={onOpenPrivacy} style={legalLinkStyle}>
        {t("auth.footerPrivacy")}
      </button>
      <span aria-hidden="true">·</span>
      <button onClick={onOpenTerms} style={legalLinkStyle}>
        {t("auth.footerTerms")}
      </button>
      <span aria-hidden="true">·</span>
      <a href="mailto:gjelirefail@gmail.com" style={{ ...legalLinkStyle, textDecoration: "none" }}>
        {t("auth.footerSupport")}
      </a>
    </div>
  );
}

const legalLinkStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  color: "var(--ink-2)",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
};

function autoCompleteFor(label: string, type: string): string {
  const l = label.toLowerCase();
  if (type === "email" || l.includes("email")) return "email";
  if (type === "password") {
    if (l.includes("new") || l.includes("create")) return "new-password";
    return "current-password";
  }
  if (l.includes("username")) return "username";
  return "off";
}
